// frontend/src/components/Auth/TermsGate.tsx
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ScrollText, AlertCircle } from 'lucide-react';
import { Button, Card } from '../ui';
import { getErrorMessage } from '../../utils/errorUtils';

export interface TermsGateProps {
  /** The signed-in email (from the verified session). */
  email: string;
  /** True when the user accepted an earlier version that is no longer valid. */
  updated: boolean;
  /** Sign the user out instead of accepting. */
  onSignOut: () => void;
  /** Record acceptance of the current terms. Throws on failure; on success
   *  the app updates the user and this screen unmounts. */
  onAccept: () => Promise<void>;
}

/**
 * Shown after sign-in whenever the user has not accepted the current Terms of
 * Service and Privacy Policy (open-access plan §3.3). The backend refuses
 * every AI call until acceptance is recorded, so the whole app waits here.
 */
const TermsGate = ({ email, updated, onSignOut, onAccept }: TermsGateProps) => {
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!agreed) {
      setError('Please agree to the Privacy Policy and Terms of Service to continue.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await onAccept();
    } catch (err: unknown) {
      setError(getErrorMessage(err) || 'We couldn’t save your agreement. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-ground flex items-center justify-center px-4 py-12">
      <Card className="w-full max-w-md p-6">
        <div className="mb-4 flex flex-col items-center text-center">
          <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-control bg-primary/10 text-primary">
            <ScrollText className="h-6 w-6" />
          </span>
          <h1 className="text-lg font-semibold text-ink">
            {updated ? 'We’ve updated our terms' : 'Before you start'}
          </h1>
          <p className="mt-2 text-sm text-ink-2 leading-relaxed">
            You&rsquo;re signed in as <span className="font-medium text-ink">{email}</span>.{' '}
            {updated
              ? 'Please review and agree to the current terms to keep using My Academic Planner.'
              : 'Please review and agree to our terms to use My Academic Planner.'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <label className="flex items-start gap-2.5 text-sm text-ink-2 cursor-pointer">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-line-soft text-primary focus:ring-primary"
            />
            <span>
              I agree to the{' '}
              <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-info underline">
                Privacy Policy
              </Link>{' '}
              and{' '}
              <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-info underline">
                Terms of Service
              </Link>
              .
            </span>
          </label>

          {error && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-control bg-status-critical/10 p-3 text-sm text-status-critical-fg"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-status-critical" />
              <span>{error}</span>
            </div>
          )}

          <Button type="submit" isLoading={submitting} disabled={submitting || !agreed} className="w-full">
            {submitting ? 'Saving…' : 'Agree and continue'}
          </Button>
        </form>

        <div className="mt-5 border-t border-line-soft pt-4 text-center">
          <Button type="button" intent="ghost" size="sm" onClick={onSignOut}>
            Sign out
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default TermsGate;
